import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaUser,
  FaFileInvoice,
  FaClipboardList,
  FaDatabase,
  FaCog,
} from "react-icons/fa";

const plans = [
  {
    // name: "Monthly (Starter)",
    name: `Vehicle Commission Agent`,
    price: `₹14,999 + GST 
    (one time subscription)`,
    tagline: "Ideal for Beginners",
    desc: "Best For Transport Billing Operation With Advanced Features in Web & Mobile View.",
    features: [
      "Masters Module Management: Groups,Financial Ledger, Products, Location , Vehicles, Bank, etc.",
      "Full Transactions module: Collection Memo / Commission Memo, Receipt/Payment Vouchers, Contra Vouchers",
      "Advanced Reports: Commission Memo, Memo, Invoice, Party/Vendor Oustanding, Profit & Loss & Balance Sheet & more...",
      "User Management (Admin user can use)",
      "Database backup utility",
    ],
    modules: ["Admins", "Masters", "Transactions", "Reports"],
    highlight: false,
  },
  {
    // name: "Yearly (Pro)",
    name: `Transport Booking & Contractor Agent`,
    price: `₹19,999 + GST 
    (one time subscription)`,
    tagline: "Most Popular",
    desc: "Best For Transport Billing Operation With Advanced Features in Web & Mobile View.",
    features: [
      "Advanced Reports: LR, Memo, Invoice, Party/Vendor Oustanding, Profit & Loss & Balance Sheet & more...",
      "Full Transactions module: LR/Hire Entry, Receipt/Payment Vouchers, Debit/Credit Notes",
      "Masters module management: Products, Tariffs, Vehicles, Bank, etc.",
      "User Management with roles & permissions (Admin + 1 user can use)",
      "Automated reminders and export utilities",
      "Priority Support",
    ],
    modules: ["Admins", "Masters", "Transactions", "Reports"],
    highlight: true,
  },

  {
    // name: "Yearly (Pro)",
    name: `Transport Booking & Contractor Agent`,
    price: `₹24,999 + GST 
    (one time subscription)`,
    tagline: "",
    desc: "Best For Transport Billing Operation With Advanced Features in Web & Mobile View.",
    features: [
      "Advanced Reports: LR, Memo, Invoice, Party/Vendor Oustanding, Profit & Loss & Balance Sheet & more...",
      "Full Transactions module: LR/Hire Entry, Receipt/Payment Vouchers, Debit/Credit Notes",
      "Masters module management: Products, Tariffs, Vehicles, Bank, etc.",
      "User Management with roles & permissions (Admin + 2 users can use)",
      "Automated reminders and export utilities",
      "Priority Support",
    ],
    modules: ["Admins", "Masters", "Transactions", "Reports"],
    highlight: false,
  },
];

const moduleIcons = {
  Admins: <FaUser className="inline mr-1 text-blue-500" />,
  Masters: <FaClipboardList className="inline mr-1 text-green-500" />,
  Transactions: <FaFileInvoice className="inline mr-1 text-yellow-500" />,
  Reports: <FaDatabase className="inline mr-1 text-purple-500" />,
  Utility: <FaCog className="inline mr-1 text-red-500" />,
};

const PricingCards = () => {
  const navigate = useNavigate();

  return (
    <section className="-mx-5 sm:-mx-24 w-[calc(100%+40px)] sm:w-[calc(100%+192px)] bg-gradient-to-b from-gray-50 via-white to-gray-50 px-2 sm:px-4 lg:px-6 py-16">
      {/* Heading */}
      <div className="text-center max-w-3xl mx-auto">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0D3559] mb-2">
          Grow Your Business Faster
        </h1>
        <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-6">
          Pick your plan today
        </p>
      </div>

      {/* Cards */}
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10 max-w-[2100px] w-full mx-auto px-1 sm:px-2 items-stretch">
        {plans.map((plan, idx) => (
          <motion.div
            key={idx}
            whileHover={{
              scale: 1.05,
              boxShadow: "0px 8px 25px rgba(13,53,89,0.25)",
            }}
            transition={{ duration: 0.2 }}
            className={`relative w-full min-w-0 h-full flex flex-col rounded-3xl p-6 sm:p-8 shadow-xl cursor-pointer
              ${plan.highlight
                ? "bg-gradient-to-br from-[#0D3559] to-[#1E4A7E] text-white scale-105 z-10"
                : "bg-white border border-gray-200 text-gray-800"
              }`}
          >
            {/* Badge */}
            {plan.tagline && (
              <span
                className={`absolute -top-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs sm:text-sm font-bold shadow
                ${plan.highlight
                    ? "bg-[#1E4A7E] text-white"
                    : "bg-gray-200 text-gray-800"
                  }`}
              >
                {plan.tagline}
              </span>
            )}

            {/* Title + Price */}
            <h2 className="text-xl sm:text-1.5xl font-bold mb-2 whitespace-pre-line">
              {plan.name}
            </h2>
            <p className="text-3xl sm:text-4xl font-extrabold mb-2 whitespace-pre-line">
              {plan.price}
            </p>
            <p
              className={`mb-4 text-sm sm:text-base ${plan.highlight ? "text-gray-200" : "text-gray-500"
                }`}
            >
              {plan.desc}
            </p>

            {/* Modules */}
            <div className="mb-4 flex flex-wrap gap-2">
              {plan.modules.map((mod) => (
                <span
                  key={mod}
                  className={`text-sm font-medium px-2 py-1 rounded-full border
                  ${plan.highlight
                      ? "border-white text-white"
                      : "border-gray-300 text-gray-700"
                    }`}
                >
                  {moduleIcons[mod]} {mod}
                </span>
              ))}
            </div>

            {/* Features */}
            <ul className="mb-6 space-y-2 text-sm sm:text-base flex-1">
              {plan.features.map((f, i) => (
                <li key={i} className="flex items-start">
                  <span
                    className={`mr-2 mt-1 ${plan.highlight ? "text-[#3B82F6]" : "text-[#0D3559]"
                      }`}
                  >
                    ✔
                  </span>
                  <span className="flex-1">{f}</span>
                </li>
              ))}
            </ul>

            {/* CTA Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/contact")}
              className={`w-full mt-auto py-3 rounded-full font-semibold text-sm sm:text-base transition
                ${plan.highlight
                  ? "bg-white text-[#0D3559] hover:bg-gray-100"
                  : "bg-[#0D3559] text-white hover:bg-[#092441]"
                }`}
            >
              {plan.highlight ? "Choose Pro" : "Get Started"}
            </motion.button>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default PricingCards;